import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { AuthLayout } from "@/components/auth-layout"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { maskEmail, ROLE_LABEL } from "@/features/workspaces/workspace-model"
import { authClient, authCode, unwrap } from "@/lib/auth-client"
import { sessionQuery, workspacesQuery } from "@/lib/queries"
import { useSignOut } from "@/lib/session"
import {
  invitationLanding,
  invitationLandingQuery,
  invitationPreviewQuery,
  myInvitationsQuery,
} from "@/lib/settings-api"
import { inviteRoute } from "@/router"

const linkButton =
  "inline-flex h-10 items-center justify-center rounded-control px-4 text-sm font-semibold no-underline"

/** The invite page's own address, with the link's key when it had one (sign-in and sign-up come back here). */
export const invitePath = (invitationId: string, key?: string): string =>
  `/invite/${invitationId}${key ? `?k=${key}` : ""}`

/**
 * Where Join goes (decision 0130, audit item 7): the project the invitation named, in its Chat with the lead (the
 * member's first step, "Say hi to Guy"; e2e 2026-10-20 open issue 5), else the workspace's Home.
 */
export const joinTarget = (
  workspaceId: string,
  landing: { readonly projectId: string | null } | null,
):
  | {
      readonly to: "/w/$workspaceId/p/$projectId"
      readonly params: { workspaceId: string; projectId: string }
      readonly search: { view: "chat" }
    }
  | { readonly to: "/w/$workspaceId"; readonly params: { workspaceId: string } } =>
  landing?.projectId
    ? {
        to: "/w/$workspaceId/p/$projectId",
        params: { workspaceId, projectId: landing.projectId },
        search: { view: "chat" },
      }
    : { to: "/w/$workspaceId", params: { workspaceId } }

const andList = (names: ReadonlyArray<string>): string =>
  names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`

/**
 * Decision 0159 (personas 14): the join question names what the person joins. "Join jung.dev to work on dictly.app?"
 * when the invitation names projects; the role only when it isn't plain membership ("as Admin").
 */
export const joinQuestion = (
  workspaceName: string,
  role: "owner" | "admin" | "member",
  projects: ReadonlyArray<string>,
): string =>
  `Join ${workspaceName}${projects.length > 0 ? ` to work on ${andList(projects)}` : ""}${
    role === "member" ? "" : ` as ${ROLE_LABEL[role]}`
  }?`

/** `/invite/$invitationId` (earlier spec §5.2): works signed in and out. */
export function InvitePage() {
  const { invitationId } = inviteRoute.useParams()
  const { k } = inviteRoute.useSearch()
  const session = useQuery(sessionQuery)
  const preview = useQuery(invitationPreviewQuery(invitationId, k))
  const back = invitePath(invitationId, k)

  if (preview.isPending || session.isPending)
    return (
      <Frame title="Invitation" description="Looking up the invitation…">
        {null}
      </Frame>
    )
  const data = preview.data
  // an invite link opened again after Join (the emailed link stays in the inbox): where they are, not "not valid"
  if (data?.status === "accepted")
    return session.data ? <AlreadyJoined invitationId={invitationId} back={back} /> : <Used back={back} />
  if (preview.isError || !data || data.status !== "pending") return <Invalid />

  if (!session.data)
    return (
      <Frame
        title={`Join ${data.workspace.name}`}
        description={`${data.inviterName} invited you to ${data.workspace.name}${data.projects?.length ? ` to work on ${andList(data.projects)}` : ""}${data.role === "member" ? "" : ` as ${ROLE_LABEL[data.role]}`}. Create an account with ${data.email ?? data.emailMasked}, or sign in with it, to join.`}
      >
        <div className="flex flex-col gap-2">
          {/* decision 0130: most invited people have no account yet, so creating one comes first */}
          <Link
            to="/sign-up"
            search={{ redirect: back }}
            className={`${linkButton} bg-brand text-brand-ink hover:bg-brand-deep hover:text-brand-ink`}
          >
            Create account
          </Link>
          <Link
            to="/sign-in"
            search={{ redirect: back }}
            className={`${linkButton} border border-line bg-surface text-ink hover:bg-ground hover:text-ink`}
          >
            Sign in
          </Link>
        </div>
      </Frame>
    )

  return (
    <SignedIn
      invitationId={invitationId}
      back={back}
      workspaceName={data.workspace.name}
      role={data.role}
      emailMasked={data.emailMasked}
      projects={data.projects ?? []}
      myEmail={session.data.user.email}
    />
  )
}

function SignedIn({
  invitationId,
  back,
  workspaceName,
  role,
  emailMasked,
  myEmail,
  projects,
}: {
  invitationId: string
  back: string
  workspaceName: string
  role: "owner" | "admin" | "member"
  emailMasked: string
  myEmail: string
  projects: ReadonlyArray<string>
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const router = useRouter()
  const signOut = useSignOut()
  const invitation = useQuery({
    queryKey: ["invitations", invitationId, "full"],
    queryFn: async () => unwrap(await authClient.organization.getInvitation({ query: { id: invitationId } })),
    retry: false,
    // "for someone else" stays until they switch accounts: no refetch on every mount
    retryOnMount: false,
  })
  const workspaces = useQuery(workspacesQuery).data?.workspaces ?? []
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: workspacesQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: myInvitationsQuery.queryKey }),
    ])
  const join = useMutation({
    mutationFn: async () => unwrap(await authClient.organization.acceptInvitation({ invitationId })),
    onSuccess: async (data) => {
      await refresh()
      // decision 0130: open the project the invitation named, not workspace Home
      if (data) await navigate(joinTarget(data.member.organizationId, await invitationLanding(invitationId)))
    },
  })
  const decline = useMutation({
    mutationFn: async () => unwrap(await authClient.organization.rejectInvitation({ invitationId })),
    onSuccess: async () => {
      await refresh()
      await navigate({ to: "/" })
    },
  })

  if (invitation.isPending)
    return (
      <Frame title={`Join ${workspaceName}`} description="Checking the invitation…">
        {null}
      </Frame>
    )
  if (invitation.isError) {
    const code = authCode(invitation.error)
    if (code === "YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION")
      return (
        <Frame
          title="This invite is for someone else"
          description={`This invite is for ${emailMasked}. You're signed in as ${maskEmail(myEmail)}.`}
        >
          <Button
            onClick={async () => {
              await signOut()
              // decision 0130: the right address usually has no account yet; sign-up links to sign-in
              await router.navigate({ to: "/sign-up", search: { redirect: back } })
            }}
          >
            Sign out and switch
          </Button>
        </Frame>
      )
    return <Invalid />
  }
  const organizationId = invitation.data?.organizationId
  const already = workspaces.find((w) => w.id === organizationId)
  if (already)
    return (
      <Frame title={`You're already in ${already.name}`} description="Nothing more to do.">
        <Link
          to="/w/$workspaceId"
          params={{ workspaceId: already.id }}
          className={`${linkButton} bg-brand text-brand-ink hover:bg-brand-deep hover:text-brand-ink`}
        >
          Open
        </Link>
      </Frame>
    )
  return (
    <Frame title={`Join ${workspaceName}`} description={joinQuestion(workspaceName, role, projects)}>
      {(join.error || decline.error) && (
        <ErrorText className="mb-3">{(join.error ?? decline.error)?.message}</ErrorText>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={join.isPending || decline.isPending}
          onClick={() => decline.mutate()}
        >
          Decline
        </Button>
        <Button disabled={join.isPending || decline.isPending} onClick={() => join.mutate()}>
          {join.isPending ? "Joining…" : "Join workspace"}
        </Button>
      </div>
    </Frame>
  )
}

/** Signed in, the invitation accepted: theirs and they are in the workspace → Open (its project), else not valid. */
function AlreadyJoined({ invitationId, back }: { invitationId: string; back: string }) {
  const landing = useQuery(invitationLandingQuery(invitationId))
  const workspaces = useQuery(workspacesQuery)
  if (landing.isPending || workspaces.isPending)
    return (
      <Frame title="Invitation" description="Checking the invitation…">
        {null}
      </Frame>
    )
  if (!landing.data) return <Used back={back} signedIn />
  const name = workspaces.data?.workspaces.find((w) => w.id === landing.data?.workspaceId)?.name
  return (
    <Frame title={name ? `You're already in ${name}` : "You're already in"} description="Nothing more to do.">
      <Link
        {...joinTarget(landing.data.workspaceId, landing.data)}
        className={`${linkButton} bg-brand text-brand-ink hover:bg-brand-deep hover:text-brand-ink`}
      >
        Open
      </Link>
    </Frame>
  )
}

/** The invitation was accepted: signed out, sign in with the account that joined; signed in as another, say so. */
function Used({ back, signedIn = false }: { back: string; signedIn?: boolean }) {
  return (
    <Frame
      title="This invite was already used"
      description={
        signedIn
          ? "It was accepted by another account. Sign in with the account that joined, or ask for a new invite."
          : "If you joined with it, sign in to open the workspace. Otherwise ask the person who invited you for a new one."
      }
    >
      {signedIn ? (
        <Link to="/" className="text-sm font-semibold">
          Go to Minga
        </Link>
      ) : (
        <Link
          to="/sign-in"
          search={{ redirect: back }}
          className={`${linkButton} bg-brand text-brand-ink hover:bg-brand-deep hover:text-brand-ink`}
        >
          Sign in
        </Link>
      )}
    </Frame>
  )
}

function Invalid() {
  return (
    <Frame
      title="This invite isn't valid anymore"
      description="Ask the person who invited you for a new one."
    >
      <Link to="/" className="text-sm font-semibold">
        Go to Minga
      </Link>
    </Frame>
  )
}

function Frame({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <AuthLayout title={title} description={description} footer={null}>
      {children}
    </AuthLayout>
  )
}
